import ICrossFrameTool from '@src/types/CrossFrameTool';
import { CrossFrameUtils } from '@src/utils/CrossFrameUtils';
import { getParameterByName } from '@src/utils/PageUtils';

const enum CrossFrameMethod {
    SetCurrentTool = 'setCurrentTool',
}

interface IRedirectToToolParams {
    fromToolId: string;
    toTool: ICrossFrameTool;
    contextParams: { [key: string]: any };
    nextTool?: ICrossFrameTool;
}

interface IRedirectToNextToolParams {
    fromToolId: string;
    contextParams: { [key: string]: any };
    nextTool?: ICrossFrameTool;
}

/**
 * Calculates the size of the provided string
 * @private
 * @param uri stringified content that will be encoded as URI parameters in the CrossFrame call
 * @returns {number} Size of the provided content string in bytes
 */
const _getUriSize = (uri: string): number => {
    return new Blob([uri]).size;
};

/**
 * Get the tool ID of the previous tool (redirected from) if it exists
 * Note: The return type of getParameterByName() is incorrect as it can technically return either a string or null
 * @returns {string | null} The tool ID of the previous tool, or null
 */
export const getPreviousToolId = (): string | null => {
    return getParameterByName('previousToolId');
};

/**
 * Get the next tool from the posted CrossFrame parameters if it was provided to redirectToTool()
 * @returns {ICrossFrameTool | undefined} The next tool or undefined
 */
export const getNextTool = (): ICrossFrameTool | undefined => {
    const nextToolParam = getParameterByName('nextTool');

    if (nextToolParam && nextToolParam !== '') {
        return JSON.parse(nextToolParam) as ICrossFrameTool;
    }
};

/**
 * Get a CrossFrame parameter from a previous redirection by name
 * @param name The name of the parameter
 * @returns {string | object} Either the deserialized param or the string value
 */
export const getCrossFrameParameterByName = (name: string) => {
    const param = getParameterByName(name);

    if (param) {
        try {
            return JSON.parse(param);
        } catch (ex) {
            // SyntaxError thrown by JSON.parse implies the presence of a plain string value (rather than encoded JSON)
            if (ex instanceof SyntaxError) {
                return param;
            } else {
                console.error('Failed to parse CrossFrame parameter:', ex);
                throw ex;
            }
        }
    }
};

/**
 * Redirect to the specified tool with a set of routing & context parameters
 * @param toToolId The tool to redirect to (IDs can be found in IhmAssociateToolRegistrations)
 * @param contextParams A string -> string KVP with any context that the tool being redirected to could consume
 * @param nextTool Tool metadata that the consuming tool can then redirect to if it is part of a managed workflow
 * @returns {Promise<unknown>}
 */
export const redirectToTool = ({ fromToolId, toTool, contextParams, nextTool }: IRedirectToToolParams) => {
    const stringifiedParams = Object.keys(contextParams).reduce((acc, param) => {
        return {
            ...acc,
            [param]: typeof contextParams[param] === 'string' ? contextParams[param] : JSON.stringify(contextParams[param]),
        };
    }, {});

    const toolParameters = {
        previousToolId: fromToolId,
        nextTool: nextTool ? JSON.stringify(nextTool) : '',
        ...stringifiedParams,
    };

    // Warn the caller if they are passing a large amount of content since these are being encoded as URI parameters
    const paramLength = _getUriSize(JSON.stringify(toolParameters));
    if (paramLength > 2000) {
        console.warn(
            '[ToolRedirectionContext] The parameters provided to redirectToTool() are greater than 2000 bytes in size and may exceed the maxium length of an iframe asset URI in certain browsers.',
        );
    }

    return CrossFrameUtils.sendMessageToOtherFrame({
        frame: window,
        message: {
            method: CrossFrameMethod.SetCurrentTool,
            data: {
                toolId: toTool.id,
                hashPath: toTool.hashPath,
                toolParameters,
            },
        },
    });
};

/**
 * Redirect to the nextTool if it was provided by the previous tool
 * @param contextParams Any contextual parameters that may be consumed by the tool being redirected to
 * @param nextTool [optional] A new toolId + route that the consuming tool can consider to be next in the workflow
 * @returns {Promise<unknown>}
 */
export const redirectToNextTool = ({ fromToolId, contextParams, nextTool }: IRedirectToNextToolParams) => {
    const toolToRedirectTo = getNextTool();
    if (toolToRedirectTo) {
        return redirectToTool({
            fromToolId,
            toTool: toolToRedirectTo,
            contextParams,
            nextTool,
        });
    } else {
        throw new Error('nextTool does not exist.');
    }
};
